import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "./LangContext";
import { MaskLineLoad } from "./Reveal";
import { IMAGES, EMAIL } from "@/data/content";

const EASE = [0.16, 1, 0.3, 1];

export default function Hero() {
  const { t } = useLang();

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative flex min-h-screen flex-col justify-between overflow-hidden px-6 pb-8 pt-24 md:px-12 md:pt-28"
    >
      {/* Editorial image partially entering the composition */}
      <motion.div
        className="pointer-events-none absolute right-0 top-0 z-0 h-full w-[62%] overflow-hidden md:w-[42%]"
        initial={{ clipPath: "inset(0 0 100% 0)" }}
        animate={{ clipPath: "inset(0 0 0% 0)" }}
        transition={{ duration: 1.4, delay: 0.15, ease: EASE }}
      >
        <motion.img
          src={IMAGES.hero}
          alt="MIDLOWS editorial"
          className="h-full w-full object-cover grayscale"
          initial={{ scale: 1.25 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.8, ease: EASE }}
        />
        <div className="absolute inset-0 bg-background/10" />
      </motion.div>

      {/* Top registration marks */}
      <motion.div
        className="relative z-10 flex items-center justify-between"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1 }}
      >
        <span className="label mix-blend-difference text-white">BOG / CO — 01</span>
        <span className="label mix-blend-difference text-white">{t.hero.stamp}</span>
      </motion.div>

      {/* Giant wordmark */}
      <div className="relative z-10 my-auto py-8">
        <h1 className="font-display font-extrabold uppercase leading-[0.8] tracking-tighter mix-blend-difference text-white text-[12vw] sm:text-[11.5vw] lg:text-[11vw]">
          <MaskLineLoad delay={0.2}>MIDLOWS</MaskLineLoad>
        </h1>
      </div>

      {/* Bottom content */}
      <div className="relative z-10 grid grid-cols-1 gap-8 md:grid-cols-12 md:items-end">
        <div className="md:col-span-7">
          <MaskLineLoad delay={0.55}>
            <h2 className="font-display text-3xl font-medium leading-[1.02] tracking-tight sm:text-4xl md:text-5xl">
              {t.hero.headline}
            </h2>
          </MaskLineLoad>
          <motion.p
            className="mt-5 max-w-md text-sm leading-relaxed text-muted-foreground md:text-base"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
          >
            {t.hero.support}
          </motion.p>
        </div>

        <div className="flex flex-col gap-6 md:col-span-5 md:items-end md:text-right">
          <motion.a
            href={`mailto:${EMAIL}?subject=Brand%20partnership`}
            data-testid="hero-cta"
            data-cursor="hover"
            className="group inline-flex items-center gap-2 self-start font-display text-lg font-medium md:self-end"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 1.05 }}
          >
            <span className="border-b border-foreground pb-1 transition-colors duration-300 group-hover:border-accent group-hover:text-accent">
              {t.hero.cta}
            </span>
            <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-accent" />
          </motion.a>
          <motion.span
            className="label"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 1.15 }}
          >
            {t.hero.location}
          </motion.span>
        </div>
      </div>
    </section>
  );
}
